import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsUpDownIcon,
  ArrowDownIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../../components/ui/Card';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';
import Loading from '../../components/ui/Loading';
import { getMsalClientId } from '../../auth/msalConfig';
import { microsoftMailAPI } from '../../api/api';
import { getMicrosoftMailAccessToken } from './microsoftMailToken';

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const MailInboxPage = () => {
  const [rows, setRows] = useState([]);
  const [nextLink, setNextLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sorting, setSorting] = useState([]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.id === selectedRowId) || null,
    [rows, selectedRowId]
  );

  const loadFirstPage = useCallback(async () => {
    if (!getMsalClientId()) {
      setError(
        'MSAL istemci kimliği tanımlı değil. .env içinde REACT_APP_MSAL_CLIENT_ID ayarlayın.'
      );
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    setNextLink(null);
    try {
      const token = await getMicrosoftMailAccessToken();
      if (!token) {
        setError(
          'Microsoft oturumu bulunamadı. Hesap sayfasından Microsoft ile giriş yapın.'
        );
        setRows([]);
        return;
      }
      const data = await microsoftMailAPI.postMailInbox({
        microsoftAccessToken: token,
        top: 20,
        nextLink: null,
      });
      if (!data?.success) {
        setError(data?.message || 'Gelen kutusu alınamadı.');
        setRows([]);
        return;
      }
      setRows(Array.isArray(data.messages) ? data.messages : []);
      setNextLink(data.next_link || null);
      setSelectedRowId(null);
    } catch (e) {
      setError(e?.message || 'Beklenmeyen bir hata oluştu.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNextPage = useCallback(async () => {
    if (!nextLink) return;
    setLoadingMore(true);
    setError(null);
    try {
      const token = await getMicrosoftMailAccessToken();
      if (!token) {
        setError('Microsoft oturumu bulunamadı.');
        return;
      }
      const data = await microsoftMailAPI.postMailInbox({
        microsoftAccessToken: token,
        top: 20,
        nextLink,
      });
      if (!data?.success) {
        setError(data?.message || 'Sonraki sayfa alınamadı.');
        return;
      }
      const more = Array.isArray(data.messages) ? data.messages : [];
      setRows((prev) => [...prev, ...more]);
      setNextLink(data.next_link || null);
    } catch (e) {
      setError(e?.message || 'Beklenmeyen bir hata oluştu.');
    } finally {
      setLoadingMore(false);
    }
  }, [nextLink]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'subject',
        header: 'Konu',
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900 dark:text-white">{getValue() || '-'}</span>
        ),
      },
      {
        id: 'from',
        header: 'Gönderen',
        accessorFn: (row) =>
          [row.from_name, row.from_address].filter(Boolean).join(' · ') || '-',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">{getValue()}</span>
        ),
      },
      {
        accessorKey: 'receivedDateTime',
        header: 'Tarih',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
            {formatDate(getValue())}
          </span>
        ),
      },
      {
        accessorKey: 'isRead',
        header: 'Okundu',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">{getValue() ? 'Evet' : 'Hayır'}</span>
        ),
      },
      {
        accessorKey: 'bodyPreview',
        header: 'Önizleme',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300 max-w-md truncate block">
            {getValue() || '-'}
          </span>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const openInOutlook = () => {
    const url = selectedRow?.webLink;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="px-6 py-6">
      <div className="mb-4">
        <h1 className="text-xl font-medium text-gray-900 dark:text-white">Gelen kutusu</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Microsoft Graph ile gelen kutunuzdaki iletiler (sayfa başına 20).
        </p>
      </div>

      {error && (
        <div
          className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-300"
          role="alert"
        >
          {error}
        </div>
      )}

      <ActionToolbar className="mb-3">
        <IconActionButton
          icon={ArrowPathIcon}
          title="Yenile"
          onClick={() => loadFirstPage()}
          disabled={loading}
        />
        <IconActionButton
          icon={ArrowTopRightOnSquareIcon}
          title="Outlook'ta aç"
          onClick={openInOutlook}
          disabled={!selectedRow?.webLink}
        />
      </ActionToolbar>

      <FilterPanel
        title="Filters"
        isOpen={filtersOpen}
        onToggle={() => setFiltersOpen((v) => !v)}
      >
        <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
          <FilterActions
            onClear={() => loadFirstPage()}
            onSearch={() => loadFirstPage()}
          />
        </div>
      </FilterPanel>

      {loading ? (
        <div className="mt-4">
          <Loading fullScreen={false} message="Gelen kutusu yükleniyor..." />
        </div>
      ) : (
        <Card className="mt-4 bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      {header.column.getCanSort() ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <ArrowUpIcon className="w-3 h-3" />,
                            desc: <ArrowDownIcon className="w-3 h-3" />,
                          }[header.column.getIsSorted()] ?? (
                            <ArrowsUpDownIcon className="w-3 h-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    Kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.original.id || row.id}
                    onClick={() => setSelectedRowId(row.original.id)}
                    onDoubleClick={() => {
                      if (row.original.webLink) {
                        window.open(row.original.webLink, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className={`cursor-pointer text-xs ${
                      selectedRowId === row.original.id
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-4 text-xs text-gray-900 dark:text-gray-100 align-top"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Card>
      )}

      {nextLink && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={loadNextPage}
            disabled={loadingMore}
            className="text-sm px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {loadingMore ? 'Yükleniyor…' : 'Sonraki sayfa'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MailInboxPage;
